import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Modal,
  Stack,
  Title,
  Text,
  Group,
  Button,
  Paper,
  ScrollArea,
  Badge,
  Tooltip,
  UnstyledButton,
  Box,
  CloseButton,
  Switch,
  Select,
  Divider,
  Alert,
  TextInput,
} from "@mantine/core";
import { notifyError, notifySuccess } from "../../utils/notify";
import {
  IconCheck,
  IconX,
  IconRefresh,
  IconAlertCircle,
  IconSearch,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import {
  Role,
  WorkflowPermissions,
  WorkflowKey,
  WORKFLOW_CATEGORIES,
  PERMISSION_CONTROLS,
  PermissionControlConfig,
  getPermissionValue,
} from "../../types/workflowPermissions";
import {
  DEFAULT_PERMISSIONS,
  ROLE_DESCRIPTIONS,
  getOverrides,
} from "../../config/defaultPermissions";
import { deepEqual } from "../../utils/deepEqual";
import { usePermissions } from "../../hooks/usePermissions";
import { SectionKey } from "../../types/featureAccess";
import styles from "./PermissionsSettings.module.css";

interface WorkflowPermissionsModalProps {
  opened: boolean;
  onClose: () => void;
  role: Role;
  salonId: string;
  initialPermissions?: WorkflowPermissions;
  onPermissionUpdate: (permissions?: WorkflowPermissions) => void;
}

const WorkflowPermissionsModal: React.FC<WorkflowPermissionsModalProps> = ({
  opened,
  onClose,
  role,
  salonId,
  initialPermissions,
  onPermissionUpdate,
}) => {
  const { batchUpdateRolePermissions, resetRolePermissions } = useApp();
  const { isAvailable } = usePermissions();

  // Hide workflows where backend reports available:false (e.g. PHI workflows
  // for salons whose business_vertical doesn't satisfy required_business_vertical).
  // These workflows are already 403-gated server-side; surfacing toggles for
  // them just confuses owners.
  const isWorkflowAvailable = useCallback(
    (workflow: WorkflowKey): boolean => {
      // Only PHI workflows currently carry an `available` flag; for any other
      // workflow `isAvailable` returns true when permissions are loaded.
      return isAvailable(workflow as SectionKey);
    },
    [isAvailable],
  );

  const visibleCategories = useMemo(
    () =>
      WORKFLOW_CATEGORIES.filter((cat) =>
        cat.workflows.some((w) => isWorkflowAvailable(w)),
      ),
    [isWorkflowAvailable],
  );

  const [selectedCategory, setSelectedCategory] = useState<string>(
    visibleCategories[0]?.id || WORKFLOW_CATEGORIES[0]?.id || "calendar",
  );

  // If the previously selected category became unavailable (e.g. business
  // vertical changed), fall back to the first visible category.
  useEffect(() => {
    if (
      visibleCategories.length > 0 &&
      !visibleCategories.some((c) => c.id === selectedCategory)
    ) {
      setSelectedCategory(visibleCategories[0].id);
    }
  }, [visibleCategories, selectedCategory]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Initialize permissions from props or defaults
  const [permissions, setPermissions] = useState<WorkflowPermissions>(() => {
    return initialPermissions || DEFAULT_PERMISSIONS[role];
  });

  // Track original permissions to detect changes
  const [originalPermissions, setOriginalPermissions] =
    useState<WorkflowPermissions>(() => {
      return initialPermissions || DEFAULT_PERMISSIONS[role];
    });

  // Update when initialPermissions changes
  useEffect(() => {
    if (initialPermissions) {
      setPermissions(initialPermissions);
      setOriginalPermissions(initialPermissions);
    }
  }, [initialPermissions]);

  // Check if there are unsaved changes using proper deep equality
  const hasChanges = useMemo(() => {
    return !deepEqual(permissions, originalPermissions);
  }, [permissions, originalPermissions]);

  // Get overrides compared to defaults
  const overrides = useMemo(() => {
    return getOverrides(role, permissions);
  }, [role, permissions]);

  const hasOverrides = Object.keys(overrides).length > 0;

  // Check if we're in search mode
  const isSearchMode = searchQuery.trim().length > 0;
  const query = searchQuery.trim().toLowerCase();

  // Get controls for the selected category (when not searching)
  const categoryControls = useMemo(() => {
    const category = WORKFLOW_CATEGORIES.find((c) => c.id === selectedCategory);
    if (!category) return [];

    return PERMISSION_CONTROLS.filter(
      (control) =>
        category.workflows.includes(control.workflow) &&
        isWorkflowAvailable(control.workflow),
    );
  }, [selectedCategory, isWorkflowAvailable]);

  // Search results grouped by category
  const searchResultsGrouped = useMemo(() => {
    if (!query) return [];

    const matching = PERMISSION_CONTROLS.filter((control) => {
      if (!isWorkflowAvailable(control.workflow)) return false;
      const inLabel = control.label.toLowerCase().includes(query);
      const inDesc = control.description?.toLowerCase().includes(query);
      const inWorkflow = control.workflow.toLowerCase().includes(query);
      return inLabel || inDesc || inWorkflow;
    });

    // Group by category
    const byCategory = new Map<string, PermissionControlConfig[]>();
    matching.forEach((control) => {
      const category = WORKFLOW_CATEGORIES.find((c) =>
        c.workflows.includes(control.workflow),
      );
      const key = category?.id ?? "other";
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key)!.push(control);
    });

    return WORKFLOW_CATEGORIES.filter((c) => byCategory.has(c.id)).map(
      (cat) => ({
        categoryId: cat.id,
        categoryLabel: cat.label,
        controls: byCategory.get(cat.id)!,
      }),
    );
  }, [query, isWorkflowAvailable]);

  // Get category stats (how many permissions are enabled)
  const getCategoryStats = useCallback(
    (categoryId: string) => {
      const category = WORKFLOW_CATEGORIES.find((c) => c.id === categoryId);
      if (!category) return { enabled: 0, total: 0 };

      const controls = PERMISSION_CONTROLS.filter((control) =>
        category.workflows.includes(control.workflow),
      );

      let enabled = 0;
      let total = controls.length;

      controls.forEach((control) => {
        const value = getPermissionValue(
          permissions,
          control.workflow,
          control.key,
        );

        if (control.type === "toggle") {
          if (value === true) enabled++;
        } else if (
          control.type === "scope" ||
          control.type === "scopeWithNone"
        ) {
          if (value === "all") enabled++;
          else if (value === "own") enabled += 0.5;
        }
      });

      return { enabled: Math.round(enabled), total };
    },
    [permissions],
  );

  // Handle permission change
  const handlePermissionChange = useCallback(
    (workflow: WorkflowKey, key: string, value: unknown) => {
      setPermissions((prev) => ({
        ...prev,
        [workflow]: {
          ...prev[workflow],
          [key]: value,
        },
      }));
    },
    [],
  );

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await batchUpdateRolePermissions(salonId, {
        role,
        permissions,
      });

      if (response?.success) {
        setOriginalPermissions(permissions);
        onPermissionUpdate(permissions);

        notifySuccess(`${role} permissions have been updated`, {
          title: "Permissions saved",
        });
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      notifyError("Failed to save permissions. Please try again.", {
        title: "Error",
        icon: <IconX size={16} />,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle reset to defaults
  const handleReset = async () => {
    setIsResetting(true);
    try {
      const response = await resetRolePermissions(salonId, role);

      if (response?.success) {
        const defaultPerms = DEFAULT_PERMISSIONS[role];
        setPermissions(defaultPerms);
        setOriginalPermissions(defaultPerms);
        onPermissionUpdate(defaultPerms);
        setShowResetConfirm(false);

        notifySuccess(`${role} permissions have been reset to defaults`, {
          title: "Permissions reset",
        });
      }
    } catch (error) {
      console.error("Error resetting permissions:", error);
      notifyError("Failed to reset permissions. Please try again.", {
        title: "Error",
        icon: <IconX size={16} />,
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Handle discard changes
  const handleDiscard = () => {
    setPermissions(originalPermissions);
  };

  // Render a permission control
  const renderControl = (control: PermissionControlConfig) => {
    const value = getPermissionValue(
      permissions,
      control.workflow,
      control.key,
    );
    const defaultValue = getPermissionValue(
      DEFAULT_PERMISSIONS[role],
      control.workflow,
      control.key,
    );
    if (value === undefined) return null;
    const isOverridden = value !== defaultValue;

    if (control.type === "toggle") {
      return (
        <Box
          key={`${control.workflow}-${control.key}`}
          className={styles.permissionItem}
        >
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={2} style={{ flex: 1 }}>
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  {control.label}
                </Text>
                {isOverridden && (
                  <Badge size="xs" color="teal" variant="dot">
                    Custom
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {control.description}
              </Text>
            </Stack>
            <Switch
              checked={value as boolean}
              onChange={(e) =>
                handlePermissionChange(
                  control.workflow,
                  control.key,
                  e.currentTarget.checked,
                )
              }
              color="teal"
            />
          </Group>
        </Box>
      );
    }

    if (control.type === "scope" || control.type === "scopeWithNone") {
      return (
        <Box
          key={`${control.workflow}-${control.key}`}
          className={styles.permissionItem}
        >
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={500}>
                {control.label}
              </Text>
              {isOverridden && (
                <Badge size="xs" color="teal" variant="dot">
                  Custom
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {control.description}
            </Text>
            <Select
              data={control.options || []}
              value={value as string}
              onChange={(newValue) =>
                handlePermissionChange(control.workflow, control.key, newValue)
              }
              size="sm"
              style={{ maxWidth: 250 }}
              allowDeselect={false}
            />
          </Stack>
        </Box>
      );
    }

    return null;
  };

  const selectedCategoryData = WORKFLOW_CATEGORIES.find(
    (c) => c.id === selectedCategory,
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      classNames={{
        body: styles.modalBody,
        content: styles.modalContent,
      }}
    >
      <Stack gap={0} h="100dvh">
        {/* Header */}
        <Box className={styles.modalHeader}>
          <Group justify="space-between" align="center" w="100%">
            <Group gap="xs">
              <Title order={4}>Permissions</Title>
              <Badge color="teal" variant="light" size="lg">
                {role}
              </Badge>
              {hasChanges && (
                <Badge color="orange" variant="light" size="sm">
                  Unsaved changes
                </Badge>
              )}
            </Group>
            <Group gap="sm">
              {hasOverrides && (
                <Tooltip label="Reset all permissions to defaults">
                  <Button
                    variant="subtle"
                    color="gray"
                    size="xs"
                    leftSection={<IconRefresh size={14} />}
                    onClick={() => setShowResetConfirm(true)}
                    disabled={isSaving || isResetting}
                  >
                    Reset to defaults
                  </Button>
                </Tooltip>
              )}
              <CloseButton
                onClick={onClose}
                size="lg"
                aria-label="Close modal"
              />
            </Group>
          </Group>
        </Box>

        {/* Reset confirmation alert */}
        {showResetConfirm && (
          <Alert
            color="orange"
            icon={<IconAlertCircle size={16} />}
            withCloseButton
            onClose={() => setShowResetConfirm(false)}
            m="md"
            mb={0}
          >
            <Group justify="space-between" align="center">
              <Text size="sm">
                Reset all {role} permissions to defaults? This cannot be undone.
              </Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setShowResetConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  color="orange"
                  onClick={handleReset}
                  loading={isResetting}
                >
                  Reset
                </Button>
              </Group>
            </Group>
          </Alert>
        )}

        {/* Main content — layout (incl. mobile stacking) lives in the CSS module */}
        <Box className={styles.mainContent}>
          {/* Categories sidebar */}
          <Stack gap="sm" className={styles.sidebar}>
            {/* Search input */}
            <TextInput
              placeholder="Search permissions..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              size="sm"
            />
            <Paper withBorder style={{ flex: 1, overflow: "hidden" }}>
              <ScrollArea h="100%">
                <Stack gap={0}>
                  {visibleCategories.map((category) => {
                    const stats = getCategoryStats(category.id);
                    const isSelected =
                      category.id === selectedCategory && !isSearchMode;

                    return (
                      <UnstyledButton
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setSearchQuery("");
                        }}
                        className={styles.categoryButton}
                        data-selected={isSelected}
                      >
                        <Group justify="space-between" w="100%">
                          <Text
                            size="sm"
                            fw={isSelected ? 600 : 400}
                            c={isSelected ? "teal" : undefined}
                          >
                            {category.label}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {stats.enabled} / {stats.total}
                          </Text>
                        </Group>
                      </UnstyledButton>
                    );
                  })}
                </Stack>
              </ScrollArea>
            </Paper>
          </Stack>

          {/* Permissions list */}
          <Paper withBorder p="md" className={styles.permissionsPane}>
            <Stack gap="sm" h="100%">
              {isSearchMode ? (
                /* Search results header */
                <Group justify="space-between">
                  <Stack gap={2}>
                    <Text fw={500} size="lg">
                      Search Results
                    </Text>
                    <Text size="xs" c="dimmed">
                      {searchResultsGrouped.reduce(
                        (acc, g) => acc + g.controls.length,
                        0,
                      )}{" "}
                      permission(s) found for "{searchQuery}"
                    </Text>
                  </Stack>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear search
                  </Button>
                </Group>
              ) : (
                /* Category header */
                <Group justify="space-between">
                  <Stack gap={2}>
                    <Text fw={500} size="lg">
                      {selectedCategoryData?.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {selectedCategoryData?.description}
                    </Text>
                  </Stack>
                </Group>
              )}

              <Divider />

              <ScrollArea style={{ flex: 1 }} offsetScrollbars>
                {isSearchMode ? (
                  /* Search results grouped by category */
                  <Stack gap="lg">
                    {searchResultsGrouped.length === 0 ? (
                      <Text size="sm" c="dimmed" ta="center" py="xl">
                        No permissions found matching "{searchQuery}"
                      </Text>
                    ) : (
                      searchResultsGrouped.map((group) => (
                        <Stack key={group.categoryId} gap="sm">
                          <Text size="sm" fw={600} c="dimmed">
                            {group.categoryLabel}
                          </Text>
                          {group.controls.map((control) =>
                            renderControl(control),
                          )}
                        </Stack>
                      ))
                    )}
                  </Stack>
                ) : (
                  /* Category permissions */
                  <Stack gap="md">
                    {categoryControls.length === 0 ? (
                      <Text size="sm" c="dimmed" ta="center" py="xl">
                        No configurable permissions in this category
                      </Text>
                    ) : (
                      categoryControls.map((control) => renderControl(control))
                    )}
                  </Stack>
                )}
              </ScrollArea>
            </Stack>
          </Paper>
        </Box>

        {/* Footer with save button */}
        <Box className={styles.modalFooter}>
          <Group justify="space-between" w="100%">
            <Text size="xs" c="dimmed">
              {ROLE_DESCRIPTIONS[role].description}
            </Text>
            <Group gap="sm">
              {hasChanges && (
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={handleDiscard}
                  disabled={isSaving}
                >
                  Discard changes
                </Button>
              )}
              <Button
                color="teal"
                onClick={handleSave}
                loading={isSaving}
                disabled={!hasChanges}
                leftSection={!isSaving && <IconCheck size={16} />}
              >
                Save changes
              </Button>
            </Group>
          </Group>
        </Box>
      </Stack>
    </Modal>
  );
};

export default WorkflowPermissionsModal;
